import { FC, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { generatePath, Link } from 'react-router'

import { Col, Flex, Row, Select, theme, Tooltip, Typography } from 'antd'
import { useResponsive } from 'antd-style'

import { useCountries } from '../../components/country'
import { Label } from '../../components/country/CountrySelect'
import { Country } from '../../components/icon/Country'
import { Title } from '../../components/text/Title'
import routes from '../../routes'

type RaceYearTableProps = { data?: Maybe<RiderRaceResultFragment>[] }
type TrackMap = Record<string, Pick<TrackCardFragment, 'country' | 'name' | 'slug'>>

const { useToken } = theme

type FilterType = 'wins' | 'no-wins' | 'podiums' | 'no-podiums' | 'dnf' | 'no-dnf'

const Statistics: FC<RaceYearTableProps> = ({ data = [] }) => {
  const { token } = useToken()
  const { desktop } = useResponsive()
  const { getNameByIso } = useCountries()
  const { formatMessage } = useIntl()

  const years: Record<number, RiderRaceResultFragment[]> = {}
  const tracks: TrackMap = {}
  const winTracks: TrackMap = {}
  const podiumTracks: TrackMap = {}
  const dnfTracks: TrackMap = {}
  const [selected, setSelected] = useState<string[]>([])
  const [filter, setFilter] = useState<FilterType>()

  data?.forEach((it) => {
    if (it?.year) {
      if (!years[it.year]) {
        years[it.year] = []
      }
      years[it.year].push(it)
    }
    if (it?.track?.slug && !tracks[it?.track?.slug]) {
      tracks[it.track?.slug] = it?.track
    }
    if (it?.track?.slug && it.didNotFinish && !dnfTracks[it?.track?.slug]) {
      dnfTracks[it.track?.slug] = it?.track
    }
    if (it?.track?.slug && it.position === 1 && !winTracks[it?.track?.slug]) {
      winTracks[it.track?.slug] = it?.track
    }
    if (it?.track?.slug && !!it.position && it.position < 4 && !it.didNotFinish && !podiumTracks[it?.track?.slug]) {
      podiumTracks[it.track?.slug] = it?.track
    }
  })

  const filterTracks = (filter: FilterType): TrackMap => {
    const noWinTracksKeys = Object.keys(tracks).filter((it) => !Object.keys(winTracks).includes(it))
    const noDnfTracksKeys = Object.keys(tracks).filter((it) => !Object.keys(dnfTracks).includes(it))
    const noPodiumTrackKeys = Object.keys(tracks).filter((it) => !Object.keys(podiumTracks).includes(it))
    const noWinTracks: TrackMap = Object.values(tracks)
      .filter((it) => noWinTracksKeys.includes(it.slug))
      .reduce((acc, each) => ({ ...acc, [`${each.slug}`]: each }), {})
    const noPodiumTracks: TrackMap = Object.values(tracks)
      .filter((it) => noPodiumTrackKeys.includes(it.slug))
      .reduce((acc, each) => ({ ...acc, [`${each.slug}`]: each }), {})
    const noDnfTracks: TrackMap = Object.values(tracks)
      .filter((it) => noDnfTracksKeys.includes(it.slug))
      .reduce((acc, each) => ({ ...acc, [`${each.slug}`]: each }), {})

    switch (filter) {
      case 'wins':
        return winTracks
      case 'no-wins':
        return noWinTracks
      case 'podiums':
        return podiumTracks
      case 'no-podiums':
        return noPodiumTracks
      case 'dnf':
        return dnfTracks
      case 'no-dnf':
        return noDnfTracks
      default:
        return tracks
    }
  }

  if (!data) return null

  return (
    <Flex vertical gap={16}>
      <Title level={3}>
        <FormattedMessage id={'rider.stats'} />
      </Title>
      <Row gutter={[16, 16]} style={{ width: '100%' }}>
        <Col span={24} lg={16}>
          <Select<string[]>
            style={{ width: '100%' }}
            placeholder={formatMessage({ id: 'track.search.placeholder' })}
            size={'middle'}
            defaultValue={selected}
            mode={'tags'}
            onChange={setSelected}
            options={Object.values(filter ? filterTracks(filter) : tracks).map((it) => ({
              label: (
                <div style={{ padding: desktop ? 2 : 0 }}>
                  <Label code={it?.country} name={it.name} />
                </div>
              ),
              value: it.slug,
            }))}
          />
        </Col>
        <Col span={24} lg={8}>
          <Select<'wins' | 'no-wins'>
            placeholder={formatMessage({ id: 'track.filter.position' })}
            style={{ width: '100%' }}
            allowClear
            size={'middle'}
            onChange={setFilter}
            options={[
              { label: formatMessage({ id: 'track.filter.wins' }), value: 'wins' },
              { label: formatMessage({ id: 'track.filter.no-wins' }), value: 'no-wins' },
              { label: formatMessage({ id: 'track.filter.podiums' }), value: 'podiums' },
              { label: formatMessage({ id: 'track.filter.no-podiums' }), value: 'no-podiums' },
              { label: formatMessage({ id: 'track.filter.no-dnf' }), value: 'no-dnf' },
              { label: formatMessage({ id: 'track.filter.dnf' }), value: 'dnf' },
            ]}
          />
        </Col>
      </Row>
      <Flex vertical gap={!desktop ? 8 : 0}>
        {Object.entries(years)
          ?.slice()
          .reverse()
          .map(([year, results]) => (
            <Row key={year} justify={'center'} align={'middle'} wrap={false} style={{ minHeight: 52 }}>
              <Col flex={'60px'}>
                <Typography.Text code>{year}</Typography.Text>
              </Col>
              <Col flex={'auto'}>
                <Row style={{ width: '100%' }}>
                  {results
                    .filter((it) => (selected.length > 0 ? selected.includes(`${it.track?.slug}`) : it))
                    .filter((it) => (filter ? Object.keys(filterTracks(filter)).includes(`${it.track?.slug}`) : it))
                    ?.map(
                      (it) =>
                        it && (
                          <Col style={{ minWidth: 50, margin: 1 }} span={1} key={it.documentId}>
                            <Tooltip
                              title={
                                <Flex align={'center'} vertical>
                                  <Typography.Text ellipsis>
                                    <Link
                                      to={generatePath(routes.result, {
                                        track: `${it.track?.slug}`,
                                        year: `${it.year}`,
                                        category: 'motogp',
                                      })}
                                    >
                                      {it.track?.name}
                                    </Link>
                                  </Typography.Text>
                                  <Typography.Text>{getNameByIso(it.track?.country)}</Typography.Text>
                                </Flex>
                              }
                            >
                              <Flex
                                align={'center'}
                                justify={'center'}
                                vertical
                                gap={2}
                                style={{
                                  paddingTop: 8,
                                  background:
                                    it.position === 1
                                      ? token.colorErrorBgHover
                                      : it.position === 2
                                        ? token.colorWarningBgHover
                                        : it.position === 3
                                          ? token.colorSuccessBgHover
                                          : token.colorBgTextHover,
                                }}
                              >
                                <Country height={16} country={it.track?.country} />
                                <Typography.Text strong={!it.didNotFinish}>
                                  {it.didNotFinish ? 'DNF' : (it.position ?? '-')}
                                </Typography.Text>
                              </Flex>
                            </Tooltip>
                          </Col>
                        ),
                    )}
                </Row>
              </Col>
            </Row>
          ))}
      </Flex>
    </Flex>
  )
}

export { Statistics }
